import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import EPageHeader from '@/components/everridge/EPageHeader';
import ESearchInput from '@/components/everridge/ESearchInput';
import OwnersPageColumns from '@/features/owners/components/OwnersPageColumns';
import OwnerDetails from '@/features/owners/components/OwnersDetails/OwnerDetails';
import { useOwnersListApi } from '@/features/owners/hooks/useOwnersListApi';
import ETableWithApiPagination from '@/components/everridge/table/ETableWithApiPagination';
import { Button } from '@/components/ui/button';
import FilterIcon from '@/components/icons/FilterIcon';
import OwnersPageFilter from '@/features/owners/components/OwnersPageFilter';
import { useOwnersFilter } from '@/features/owners/hooks/useOwnersFilter';
import { FilterHelper } from '@/utils/filterHelper';
import { cn } from '@/lib/utils';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import OwnersListMobile from '@/features/owners/components/OwnersList/OwnersListMobile';
import NewOrder from '@/features/orders/components/NewOrder/NewOrder';
import SendIcon from '@/components/icons/SendIcon';
import OwnersInvitation from '@/features/owners/components/OwnersInvitation/OwnersInvitation';
import { useUserRole } from '@/features/identity/hooks/useUserRole.ts';
import { Roles } from '@/@types/auth.types';

const OwnersPage = () => {
	const [ownerId, setOwnerId] = useState('');
	const [searchText, setSearchText] = useState('');
	const [page, setPage] = useState(0);
	const [showOwnerDetails, setShowOwnerDetails] = useState(false);
	const [showOwnersFilter, setShowOwnersFilter] = useState(false);
	const [showOwnersInvitation, setShowOwnersInvitation] = useState(false);
	const [showNewOrder, setShowNewOrder] = useState(false);

	const { t } = useTranslation();
	const columns = OwnersPageColumns();
	const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
	const userRole = useUserRole();

	const { ownersListData } = useOwnersListApi({ searchText });

	const { filter, setFilter } = useOwnersFilter();

	const handlePageChange = (newPage: number) => {
		setPage(newPage);
		setFilter({
			skip: filter.take * newPage,
		});
	};

	const handleShowOwnerDetails = (id: string) => {
		setOwnerId(id);
		setShowOwnerDetails(true);
	};

	const handleSearchText = (text: string) => {
		setPage(0);
		setSearchText(text);
	};

	const handleNewOrder = () => {
		setShowNewOrder(true);
	};

	const filtersQuantity = FilterHelper.countFilterValues(filter, [
		'orderBy',
		'orderDirection',
		'skip',
		'take',
	]);
	
	return (
		<section className={'h-full flex flex-col gap-6'}>
			<EPageHeader
				title={t('main.owners.title')}
				search={
					<ESearchInput
						value={searchText}
						setValue={handleSearchText}
						placeholder={t('main.owners.form.search.placeholder')}
						containerClassName={cn(!isMobile && 'w-60')}
					/>
				}>
				<div className='relative'>
					{!!filtersQuantity && (
						<div className='absolute -top-1 right-0 size-4 rounded-full bg-error-1000 flex items-center justify-center text-2xs text-popover-10 animate-pulse '>
							{filtersQuantity}
						</div>
					)}
					<Button
						variant='outline'
						size={isMobile ? 'icon' : 'default'}
						onClick={() => setShowOwnersFilter(true)}>
						<FilterIcon />
						{!isMobile && t('elements.buttons.filter')}
					</Button>
				</div>
				{userRole === Roles.admin && (
					<Button
						variant='outline'
						size={isMobile ? 'icon' : 'default'}
						onClick={() => setShowOwnersInvitation(true)}>
						<SendIcon />
						{!isMobile && t('elements.buttons.sendInvitation')}
					</Button>
				)}
			</EPageHeader>
			{isMobile ? (
				<OwnersListMobile
					data={ownersListData}
					paginationStep={filter.take}
					page={page}
					setPage={handlePageChange}
					showDetails={handleShowOwnerDetails}
					createOrder={handleNewOrder}
				/>
			) : (
				<ETableWithApiPagination
					columns={columns}
					data={ownersListData}
					paginationStep={filter.take}
					page={page}
					setPage={handlePageChange}
					showDetails={handleShowOwnerDetails}
				/>
			)}
			{showOwnerDetails && (
				<OwnerDetails
					id={ownerId}
					onClose={setShowOwnerDetails}
				/>
			)}
			{showOwnersFilter && (
				<OwnersPageFilter onClose={() => setShowOwnersFilter(false)} />
			)}
			{showNewOrder && (
				<NewOrder onClose={() => setShowNewOrder(false)} />
			)}
			{showOwnersInvitation && (
				<OwnersInvitation
					onClose={() => setShowOwnersInvitation(false)}
				/>
			)}
		</section>
	);
};

export default OwnersPage;
